import React, { type ReactNode } from 'react';
import type { NQMMRankingRow } from '../../services/api';
import {
  buildNQMMOrderHandoff,
  formatNQMMPercent,
  formatNQMMPrice,
  formatNQMMQuantityChange,
} from './nqmmStrategyPresentation';

interface NQMMCandidateTableProps {
  title: string;
  icon: ReactNode;
  candidates: NQMMRankingRow[];
  rebalanceWindowOpen: boolean;
}

const NQMMCandidateTable: React.FC<NQMMCandidateTableProps> = ({
  title,
  icon,
  candidates,
  rebalanceWindowOpen,
}) => {
  const prepareOrder = (row: NQMMRankingRow, action: 'BUY' | 'SELL', quantity: number) => {
    window.dispatchEvent(new CustomEvent('nqmm-prepare-order', {
      detail: buildNQMMOrderHandoff(row, action, quantity),
    }));
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">{icon}<h3 className="text-xs font-bold uppercase tracking-wider text-white/90">{title}</h3></div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
          <span className="text-emerald-300">{candidates.filter((candidate) => (candidate.rebalance_quantity ?? 0) > 0).length} buy</span>
          <span className="text-rose-300">{candidates.filter((candidate) => (candidate.rebalance_quantity ?? 0) < 0).length} sell</span>
        </div>
      </div>
      {candidates.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted">
          {rebalanceWindowOpen
            ? 'No buy or sell orders are needed for the scheduled target.'
            : 'Order suggestions appear during the scheduled rebalance window. Momentum rankings refresh daily.'}
        </div>
      ) : (
        <div className="max-h-80 overflow-x-auto overflow-y-auto ux-scrollbar">
          <table className="w-full min-w-[620px] text-[11px]">
            <thead className="sticky top-0 bg-[#111827] text-[9px] uppercase tracking-wider text-muted">
              <tr>
                <th className="px-3 py-2 text-left">Symbol</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="px-3 py-2 text-right">Target</th>
                <th className="px-3 py-2 text-right">Current</th>
                <th className="px-3 py-2 text-right">Change</th>
                <th className="px-3 py-2 text-right">Action</th>
                <th className="px-3 py-2 text-right">Momentum</th>
                <th className="sticky right-0 bg-[#111827] px-3 py-2 text-right">Order</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((row) => {
                const currentQuantity = row.current_quantity ?? row.position?.shares ?? 0;
                const targetQuantity = row.target_quantity ?? 0;
                const quantityChange = row.rebalance_quantity ?? targetQuantity - currentQuantity;
                const orderAction = quantityChange > 0 ? 'BUY' : 'SELL';
                const orderQuantity = Math.abs(quantityChange);
                const canPrepareOrder = quantityChange !== 0;
                return (
                  <tr key={`rebalance-${row.symbol}`} className="border-t border-white/5 hover:bg-white/3">
                    <td className="px-3 py-1.5">
                      <div className="font-semibold text-white">{row.symbol}</div>
                      <div className="text-[9px] text-muted">Rank {row.rank ?? '—'} · {row.candidate_reason || 'Momentum candidate'}</div>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="font-mono text-[10px] text-white">{formatNQMMPrice(row.current_price)}</div>
                      <div className={`text-[8px] uppercase tracking-wider ${row.price_status === 'LIVE' ? 'text-emerald-300' : row.price_status === 'UNAVAILABLE' ? 'text-rose-300' : 'text-amber-300'}`}>{row.price_status || '—'}</div>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-white/70">{targetQuantity}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-white/70">{currentQuantity}</td>
                    <td className={`px-3 py-1.5 text-right font-mono font-semibold ${quantityChange > 0 ? 'text-emerald-300' : quantityChange < 0 ? 'text-rose-300' : 'text-white/50'}`}>
                      {formatNQMMQuantityChange(quantityChange)}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-semibold uppercase tracking-wider ${orderAction === 'SELL' ? 'text-rose-300' : 'text-emerald-300'}`}>
                      {orderAction} {orderQuantity}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono ${Number(row.momentum) >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{formatNQMMPercent(row.momentum)}</td>
                    <td className="sticky right-0 bg-[#111827] px-3 py-1.5 text-right">
                      {canPrepareOrder ? (
                        <button
                          type="button"
                          onClick={() => prepareOrder(row, orderAction, orderQuantity)}
                          className={`rounded-md border px-2 py-1 text-[9px] font-bold uppercase tracking-wider transition ${orderAction === 'BUY' ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20' : 'border-rose-400/30 bg-rose-400/10 text-rose-300 hover:bg-rose-400/20'}`}
                          title={`Review an IBKR MIDPRICE ${orderAction} rebalance order in a modal`}
                        >
                          {orderAction} MID
                        </button>
                      ) : (
                        <span className="text-[9px] uppercase tracking-wider text-white/30">No order</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NQMMCandidateTable;
